
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, ArrowDownCircle, ArrowUpCircle, CalendarDays, Hash, UserPlus, UserMinus, TrendingDown, Plus, Minus, RefreshCcw, Sparkles, Sword } from "lucide-react";
import { formatNumber } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

// Define the type for a stake event based on your table structure
type StakeEvent = {
  id: number;
  provisioner_address: string;
  event_type: 'stake' | 'unstake' | 'new_node' | 'node_exit' | 'large_stake_increase' | 'large_stake_decrease' | 'profit_taking' | 'restake' | 'first_time_node';
  previous_stake_amount: number;
  new_stake_amount: number;
  change_amount: number;
  timestamp: string;
  // Additional fields for smart events
  old_stake?: number;
  stake_difference?: number;
  related_event_id?: number;
};

// Helper function to get event display info
const getEventDisplayInfo = (event: StakeEvent) => {
  switch (event.event_type) {
    case 'restake':
      return {
        icon: <RefreshCcw className="h-6 w-6 text-purple-500 mt-1 flex-shrink-0" />,
        title: 'Re-stake Event',
        color: 'text-purple-600',
        description: event.stake_difference && event.stake_difference > 0 
          ? `Increased stake by ${formatNumber(event.stake_difference)} DUSK` 
          : event.stake_difference && event.stake_difference < 0
          ? `Decreased stake by ${formatNumber(Math.abs(event.stake_difference))} DUSK`
          : `Re-staked ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'first_time_node':
      return {
        icon: <Sparkles className="h-6 w-6 text-cyan-500 mt-1 flex-shrink-0" />,
        title: 'First-Time Node',
        color: 'text-cyan-600',
        description: `New validator joined with ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'new_node':
      return {
        icon: <UserPlus className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />,
        title: 'Node Rejoined',
        color: 'text-green-600',
        description: `Returned with stake: ${formatNumber(event.new_stake_amount)} DUSK`
      };
    case 'node_exit':
      return {
        icon: <UserMinus className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />,
        title: 'Node Left Network',
        color: 'text-red-600',
        description: `Removed stake: ${formatNumber(event.previous_stake_amount)} DUSK`
      };
    case 'large_stake_increase':
      return {
        icon: <Plus className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />,
        title: 'Large Stake Increase',
        color: 'text-green-600',
        description: `+${formatNumber(event.change_amount)} DUSK`
      };
    case 'large_stake_decrease':
      return {
        icon: <Sword className="h-6 w-6 text-orange-500 mt-1 flex-shrink-0" />,
        title: 'Node Slashed',
        color: 'text-orange-600',
        description: `-${formatNumber(event.change_amount)} DUSK`
      };
    case 'profit_taking':
      return {
        icon: <TrendingDown className="h-6 w-6 text-blue-500 mt-1 flex-shrink-0" />,
        title: 'Claim Rewards',
        color: 'text-blue-600',
        description: `Rewards withdrawn: ${formatNumber(Math.abs(event.previous_stake_amount - event.new_stake_amount))} DUSK`
      };
    // Legacy event types
    case 'stake':
      return {
        icon: <ArrowUpCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />,
        title: 'Stake Increase',
        color: 'text-green-600',
        description: `+${formatNumber(event.change_amount)} DUSK`
      };
    case 'unstake':
      return {
        icon: <ArrowDownCircle className="h-6 w-6 text-red-500 mt-1 flex-shrink-0" />,
        title: 'Stake Decrease',
        color: 'text-red-600',
        description: `-${formatNumber(event.change_amount)} DUSK`
      };
    default:
      return {
        icon: <ArrowUpCircle className="h-6 w-6 text-gray-500 mt-1 flex-shrink-0" />,
        title: 'Network Event',
        color: 'text-gray-600',
        description: `${formatNumber(event.change_amount)} DUSK`
      };
  }
};

// Helper function to detect if a node is truly new (first time ever)
const detectSmartEvents = async (rawEvents: any[]): Promise<StakeEvent[]> => {
  const events = rawEvents.map(event => ({
    ...event,
    event_type: event.event_type as StakeEvent['event_type'],
    change_amount: event.change_amount || Math.abs(event.new_stake_amount - event.previous_stake_amount)
  })) as StakeEvent[];

  const smartEvents: StakeEvent[] = [];
  const processedEventIds = new Set<number>();
  
  // Sort events by timestamp for chronological processing
  const sortedEvents = [...events].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  
  for (let i = 0; i < sortedEvents.length; i++) {
    const event = sortedEvents[i];
    
    if (processedEventIds.has(event.id)) continue;
    
    // Look for re-stake patterns: node_exit followed by large_stake_increase within 10 minutes
    if (event.event_type === 'node_exit') {
      const exitTime = new Date(event.timestamp).getTime();
      
      // Look for a corresponding stake increase within 10 minutes
      const restakeEvent = sortedEvents.find(e => 
        e.provisioner_address === event.provisioner_address &&
        e.event_type === 'large_stake_increase' &&
        !processedEventIds.has(e.id) &&
        Math.abs(new Date(e.timestamp).getTime() - exitTime) <= 10 * 60 * 1000 // 10 minutes
      );
      
      if (restakeEvent) {
        // Create consolidated re-stake event
        const stakeDifference = restakeEvent.new_stake_amount - event.previous_stake_amount;
        smartEvents.push({
          ...restakeEvent,
          event_type: 'restake',
          old_stake: event.previous_stake_amount,
          stake_difference: stakeDifference,
          related_event_id: event.id
        });
        
        processedEventIds.add(event.id);
        processedEventIds.add(restakeEvent.id);
        continue;
      }
    }
    
    // Detect first-time nodes vs returning nodes
    if (event.event_type === 'large_stake_increase' && event.previous_stake_amount === 0) {
      // Check if this address has any historical events (indicating returning node)
      const { data: historicalEvents } = await supabase
        .from('stake_events')
        .select('id')
        .eq('provisioner_address', event.provisioner_address)
        .lt('timestamp', event.timestamp)
        .limit(1);
      
      if (!historicalEvents || historicalEvents.length === 0) {
        // No previous history - this is a first-time node
        smartEvents.push({
          ...event,
          event_type: 'first_time_node'
        });
      } else {
        // Has history - this is a returning node
        smartEvents.push({
          ...event,
          event_type: 'new_node'
        });
      }
      processedEventIds.add(event.id);
      continue;
    }
    
    // Keep other events as-is if not processed
    if (!processedEventIds.has(event.id)) {
      smartEvents.push(event);
    }
  }
  
  return smartEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};

export function EventTimeline() {
  const { data: events, isLoading, error } = useQuery({
    queryKey: ["stakeEvents"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('stake_events')
        .select('*')
        .order('timestamp', { ascending: false })
        .limit(50); // Get more events to better detect patterns

      if (error) throw error;
      
      // Apply smart event detection
      return await detectSmartEvents(data || []);
    },
    refetchInterval: 60000, // Refresh every minute
  });

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Network Event Timeline</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="flex items-center space-x-4">
              <Skeleton className="h-8 w-8 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-4 w-[250px]" />
                <Skeleton className="h-4 w-[200px]" />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Network Event Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Failed to load network events: {error.message}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  if (!events || events.length === 0) {
    return (
      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-xl font-bold">Network Event Timeline</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No significant network events to display recently.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <CardTitle className="text-xl font-bold">Network Event Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {events.slice(0, 20).map((event) => {
            const eventInfo = getEventDisplayInfo(event);
            
            return (
              <div key={event.id} className="flex items-start space-x-3">
                {eventInfo.icon}
                <div className="flex-grow">
                  <div className="flex items-center justify-between">
                    <span className={`text-sm font-semibold ${eventInfo.color}`}>
                      {eventInfo.title}
                    </span>
                    <span className="text-xs text-muted-foreground flex items-center">
                      <CalendarDays className="h-3 w-3 mr-1" />
                      {new Date(event.timestamp).toLocaleDateString()} {new Date(event.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    <Hash className="inline h-3 w-3 mr-1" />
                    Provider <code className="font-mono text-xs bg-muted px-1 py-0.5 rounded">{event.provisioner_address.slice(0, 8)}...{event.provisioner_address.slice(-8)}</code>
                  </p>
                  <p className="text-md mt-1">
                    {eventInfo.description}
                  </p>
                  {event.event_type === 'restake' && event.old_stake && (
                    <p className="text-xs text-muted-foreground">
                      Previous stake: {formatNumber(event.old_stake)} DUSK → New stake: {formatNumber(event.new_stake_amount)} DUSK
                    </p>
                  )}
                  {event.event_type !== 'new_node' && event.event_type !== 'first_time_node' && event.event_type !== 'node_exit' && event.event_type !== 'profit_taking' && event.event_type !== 'restake' && (
                    <p className="text-xs text-muted-foreground">
                      New total stake: {formatNumber(event.new_stake_amount)} DUSK
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
